import { ReactNode, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { AlertModalProps } from "../components/Shared/AlertModal/AlertModal";
import AlertLevel from "../models/alert";

export type SupportAlert = {
  code?: string;
  subject?: string;
  description?: string;
  showLink?: boolean;
  onClose?: () => void;
};

type MessageAndSupport = {
  message: string;
  support: SupportAlert;
};

const useAlert = () => {
  const intl = useIntl();

  const [alert, setAlert] = useState<{
    show: boolean;
    title?: ReactNode;
    message?: ReactNode;
    level?: AlertLevel;
  }>({
    show: false,
    title: undefined,
    message: undefined,
    level: undefined,
  });

  const [supportData, setSupportData] = useState<SupportAlert>({
    subject: intl.formatMessage({
      id: "SUPPORT.REPORT_FAILURE",
      defaultMessage: "Report a failure",
    }),
    showLink: true,
  });

  const showAlert = useCallback(
    (
      message: ReactNode = alert.message,
      title: ReactNode = alert.title,
      level: AlertLevel = AlertLevel.Error
    ) => {
      setAlert({
        show: true,
        message: message,
        title: title,
        level: level,
      });
    },
    [setAlert] // eslint-disable-line
  );

  const updateSupportData = useCallback(
    (supportData: SupportAlert) => {
      setSupportData(supportData);
    },
    [setSupportData]
  );

  const showErrorAlert = useCallback(
    (
      message: ReactNode = alert.message,
      support: SupportAlert = supportData,
      title: ReactNode = alert.title
    ) => {
      updateSupportData(support ?? {});
      showAlert(message, title, AlertLevel.Error);
    },
    [showAlert] // eslint-disable-line
  );

  const showWarningAlert = useCallback(
    (
      message: ReactNode = alert.message,
      support: SupportAlert = supportData,
      title: ReactNode = alert.title
    ) => {
      updateSupportData(support ?? {});
      showAlert(message, title, AlertLevel.Warning);
    },
    [showAlert] // eslint-disable-line
  );

  const showInfoAlert = useCallback(
    (
      message: ReactNode = alert.message,
      support: SupportAlert = supportData,
      title: ReactNode = alert.title
    ) => {
      updateSupportData(support ?? {});
      showAlert(message, title, AlertLevel.Info);
    },
    [showAlert] // eslint-disable-line
  );

  const closeAlert = useCallback(() => {
    setAlert((alert) => ({
      ...alert,
      show: false,
    }));
    supportData.onClose?.();
  }, [setAlert, supportData.onClose]);

  const getSupportData = useCallback(
    (support: SupportAlert) => {
      return { ...supportData, ...support };
    },
    [supportData]
  );

  const getAlertMessageAndSupportContent = useCallback(
    (
      id: string,
      defaultMessage?: string,
      values?: Record<string, any>,
      errorCode?: string
    ): MessageAndSupport => {
      const message = intl.formatMessage({ id, defaultMessage }, values);
      const description = intl.formatMessage(
        { id, defaultMessage },
        { br: () => " " }
      );
      const support = getSupportData({ description, code: errorCode });
      return { message, support };
    },
    [intl, getSupportData]
  );

  const alertProps: AlertModalProps = {
    title: alert.title,
    level: alert.level,
    show: alert.show,
    onClose: closeAlert,
    support: supportData,
  };

  return {
    alertProps,
    alertMessage: alert.message,
    showInfoAlert,
    showWarningAlert,
    showErrorAlert,
    closeAlert,
    getSupportData,
    getAlertMessageAndSupportContent,
  };
};

export default useAlert;
